// TemplateThumbnail — shared PNG-first template preview with a guaranteed
// non-empty fallback chain. Extracted from the wizard's TemplateGallery
// (Wave H7 / Elfsight pipeline) so the marketing /templates cards, the
// portal gallery and the wizard Browse-all all render the SAME preview.
//
// Render chain (first available wins):
//   1. Pre-rendered PNG  — `/template-thumbnails/<id>@2x.png`, generated by
//      `scripts/generate-template-thumbnails.ts` (lazy-loaded `<img>`).
//   2. <TemplateMockup>  — pure data-driven mini render (real title / field
//      labels / CTA, category palette, IntersectionObserver lazy skeleton).
//   3. Category icon tile — `getQuoteQuickIcon(defaultIcon)` else
//      `resolveCategoryIcon(category)` (which itself falls back to
//      Briefcase), so an EMPTY preview block is structurally impossible
//      even if a template lacks `defaultIcon` AND the mockup render throws.
//
// "Missing PNG" state can be CONTROLLED (the wizard gallery passes a shared
// `missing` Set so the state survives list re-renders / re-filtering) or
// UNCONTROLLED (each card tracks its own failure — fine for the marketing
// grid where the list is static).

import { Component, useState, type ReactNode } from 'react';
import type { TemplateConfig } from '@shared/templatePresets';
import TemplateMockup from '@/components/wizard/elfsight/TemplateMockup';
import { resolveCategoryIcon } from '@/components/quote-widget/CategoryIcon';
import { getCategoryStyle } from '@/lib/categoryStyles';
import { getQuoteQuickIcon } from '@/data/quoteQuickIcons';

/** Static-asset URL for a template's pre-rendered thumbnail PNG. */
export function templateThumbnailUrl(id: string): string {
  return `/template-thumbnails/${encodeURIComponent(id)}@2x.png`;
}

export interface TemplateThumbnailProps {
  template: TemplateConfig;
  /**
   * Optional shared "ids whose PNG failed to load" set (controlled mode).
   * Pass it together with `onMissing` when the surrounding list re-renders
   * (filters, refetch) so cards don't re-attempt a known-missing PNG.
   * Omit both for self-contained per-card state.
   */
  missing?: Set<string>;
  onMissing?: (id: string) => void;
}

/**
 * Last-resort tile: centered category/trade icon on a quiet light surface.
 * Rendered when the mockup itself throws (corrupt/partial template data).
 * Sized to fill whatever container the caller provides — same contract as
 * the PNG / mockup branches.
 */
function CategoryIconTile({ template }: { template: TemplateConfig }) {
  const cat = getCategoryStyle(template.category);
  const Icon =
    getQuoteQuickIcon(template.defaultIcon) ??
    resolveCategoryIcon(template.category, template.categoryIcon);
  return (
    <div
      aria-hidden="true"
      data-testid={`template-thumb-icon-${template.id}`}
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255,255,255,0.94)',
        boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.06)',
        color: cat.heroAccent,
        boxSizing: 'border-box',
      }}
    >
      <Icon size={24} strokeWidth={1.9} aria-hidden="true" />
    </div>
  );
}

interface BoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

/**
 * Tiny error boundary around the mockup render. The mockup walks
 * `template.fields` / `steps` / `results` — admin-edited or partially
 * migrated presets could in principle ship shapes it chokes on; a single
 * bad template must degrade to the icon tile, not blank the card (or take
 * the whole grid down).
 */
class MockupErrorBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/**
 * PNG-first thumbnail with mockup → icon fallback. Fills its container
 * (width/height 100%), so the caller owns sizing/cropping — the wizard
 * gallery gives it the 4:5 `.qq-tg-mockup` area, the marketing card gives
 * it a top-cropped wide thumb.
 */
export default function TemplateThumbnail({ template, missing, onMissing }: TemplateThumbnailProps) {
  // Uncontrolled fallback state — used when no shared `missing` set is given.
  const [localMissing, setLocalMissing] = useState(false);
  const pngMissing = missing ? missing.has(template.id) : localMissing;

  if (pngMissing) {
    return (
      <MockupErrorBoundary fallback={<CategoryIconTile template={template} />}>
        <TemplateMockup template={template} />
      </MockupErrorBoundary>
    );
  }

  return (
    <img
      src={templateThumbnailUrl(template.id)}
      loading="lazy"
      decoding="async"
      alt={template.name}
      draggable={false}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: 'inherit',
        display: 'block',
        userSelect: 'none',
      }}
      onError={() => {
        setLocalMissing(true);
        onMissing?.(template.id);
      }}
    />
  );
}
